// ScrollCue — small "SCROLL ↓" hint at the bottom center of the Wake
// scene. Fades out as the user scrolls past 0.05 progress. Made
// interactive: clicking it scrolls to the next chapter (about).

import { useEffect, useState } from 'react';
import { Magnetic } from './Magnetic';

export interface ScrollCueProps {
  /** Whether the active scene wants the cue visible at all. */
  isVisible: boolean;
}

function scrollToProgress(target: number) {
  if (typeof window === 'undefined') return;
  const max = document.documentElement.scrollHeight - window.innerHeight;
  if (max <= 0) return;
  const y = Math.max(0, Math.min(max, target * max));
  window.scrollTo({ top: y, behavior: 'smooth' });
}

export function ScrollCue({ isVisible }: ScrollCueProps) {
  const [opacity, setOpacity] = useState(1);
  useEffect(() => {
    if (!isVisible) {
      setOpacity(0);
      return;
    }
    if (typeof window === 'undefined') return;
    let raf = 0;
    const tick = () => {
      const p = window.__planetProgress ?? 0;
      // Visible from 0 → 0.05, then fades to 0 by 0.08.
      let next;
      if (p < 0.05) next = 1;
      else if (p > 0.08) next = 0;
      else next = 1 - (p - 0.05) / 0.03;
      setOpacity((prev) => (Math.abs(prev - next) > 0.01 ? next : prev));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [isVisible]);

  if (!isVisible && opacity < 0.01) return null;

  return (
    <div
      className="scroll-cue"
      style={{ opacity }}
      aria-hidden="true"
    >
      <Magnetic
        as="button"
        className="scroll-cue__btn"
        ariaLabel="Scroll to next chapter"
        onClick={() => scrollToProgress(0.22)}
        type="button"
        innerClassName="scroll-cue__btn-inner"
      >
        <span className="scroll-cue__label">Scroll</span>
        <span className="scroll-cue__line" aria-hidden="true" />
      </Magnetic>
    </div>
  );
}
